import { Loader2 } from "lucide-react";
import type React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Textarea,
} from "~/components/ui";
import { useGroupCreate, useGroupEdit } from "~/features/admin/groups/hooks";
import type { GroupEditFormData, GroupFormData } from "~/features/admin/groups/types";

interface EditableGroup {
  id: string;
  name: string;
  description: string;
}

interface GroupFormDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** If set, the dialog is in edit mode and prefills from this group. */
  group?: EditableGroup;
}

const FORM_ID = "group-form";

export function GroupFormDialog({ open, onOpenChange, group }: GroupFormDialogProps) {
  const { t } = useTranslation();
  const isEdit = Boolean(group);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="capitalize">
            {isEdit ? `${t("ui.edit")} ${t("model.group.modelName")}` : `${t("ui.add")} ${t("model.group.modelName")}`}
          </DialogTitle>
        </DialogHeader>
        {group ? (
          <EditBody group={group} onClose={() => onOpenChange(false)} />
        ) : (
          <CreateBody onClose={() => onOpenChange(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <span className="text-12-medium text-fg-2">{children}</span>;
}

function FormFooter({ processing }: { processing: boolean }) {
  const { t } = useTranslation();
  return (
    <DialogFooter>
      <DialogClose asChild>
        <Button type="button" variant="outline" disabled={processing}>
          {t("ui.cancel")}
        </Button>
      </DialogClose>
      <Button type="submit" form={FORM_ID} disabled={processing}>
        {processing && <Loader2 className="animate-spin" />}
        <span>{t("ui.save")}</span>
      </Button>
    </DialogFooter>
  );
}

function CreateBody({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<GroupFormData>({
    defaultValues: { name: "", description: "" },
  });

  const { submit, processing } = useGroupCreate({
    onSuccess: () => {
      reset();
      onClose();
    },
  });

  return (
    <>
      <form id={FORM_ID} onSubmit={handleSubmit(submit)} className="space-y-4">
        <div className="flex flex-col gap-1.5">
          <FieldLabel>{t("ui.name")}</FieldLabel>
          <Input {...register("name", { required: t("ui.required") })} aria-invalid={Boolean(errors.name)} />
          {errors.name && <p className="text-12-medium text-danger">{errors.name.message}</p>}
        </div>
        <div className="space-y-1.5">
          <FieldLabel>{t("ui.description")}</FieldLabel>
          <Textarea {...register("description")} rows={3} />
        </div>
      </form>
      <FormFooter processing={processing} />
    </>
  );
}

function EditBody({ group, onClose }: { group: EditableGroup; onClose: () => void }) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<GroupEditFormData>({
    defaultValues: { id: group.id, name: group.name, description: group.description },
  });

  const { submit, processing } = useGroupEdit({
    onSuccess: () => {
      reset();
      onClose();
    },
  });

  return (
    <>
      <form
        id={FORM_ID}
        onSubmit={handleSubmit((formData) => submit({ ...formData, id: group.id }))}
        className="space-y-4"
      >
        <div className="flex flex-col gap-1.5">
          <FieldLabel>{t("ui.name")}</FieldLabel>
          <Input {...register("name", { required: t("ui.required") })} aria-invalid={Boolean(errors.name)} />
          {errors.name && <p className="text-12-medium text-danger">{errors.name.message}</p>}
        </div>
        <div className="space-y-1.5">
          <FieldLabel>{t("ui.description")}</FieldLabel>
          <Textarea {...register("description")} rows={3} />
        </div>
      </form>
      <FormFooter processing={processing} />
    </>
  );
}
